import { useState } from "react";

import type {
  Catalog,
  CatalogImageModel,
  ImageAdvancedRequest,
} from "../../../api/generated/model";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import type { ProductChoice } from "../../../platform/media/ProductPicker";
import {
  STYLE_TILE,
  advancedReferences,
  keepTiles,
  tileCombinations,
  totalCombinations,
  type Picks,
} from "../ad-copies/picks";
import { ADVANCED_REFERENCES, advancedModels, canSetupCopies } from "./advanced";

/** What the Advanced mode starts from, and hands back: live keeps these across the switch. */
export interface AdvancedCarry {
  product: ProductChoice | null;
  references: PickedImage[];
  modelId: string;
  aspectRatio: string | null;
}

/** The ratio when the model offers it, else the model's default (live's `getAspectRatioData`). */
function ratioOn(model: CatalogImageModel, ratio: string | null): string {
  return ratio !== null && model.aspect_ratios.values.includes(ratio)
    ? ratio
    : model.aspect_ratios.default;
}

/**
 * The Advanced mode's draft (#234), apart from the simple composer's, as live's two forms are:
 * one product, up to five references, the Style, a model and its ratio, and the ad copy values
 * ticked in the Ad copies modal. Switching on keeps the product, the first five references, the
 * model where it serves Advanced (else the list's first) and the ratio where that model offers it
 * (live's `onSwitchMode`).
 */
export function useAdvanced(catalog: Catalog, carry: AdvancedCarry) {
  const models = advancedModels(catalog);
  const first = models.find((each) => each.id === carry.modelId) ?? models[0]!;
  const [modelId, setModelId] = useState(first.id);
  const model = models.find((each) => each.id === modelId) ?? first;
  const [aspectRatio, setAspectRatio] = useState(() => ratioOn(first, carry.aspectRatio));
  const [product, setProduct] = useState(carry.product);
  const [references, setReferences] = useState(carry.references.slice(0, ADVANCED_REFERENCES));
  const [style, setStyleText] = useState("");
  // The values confirmed in the Ad copies modal (live's `textCopies`).
  const [picks, setPicks] = useState<Picks>({});
  const chooseModel = (id: string) => {
    const next = models.find((each) => each.id === id);
    if (next === undefined) return;
    setModelId(next.id);
    setAspectRatio((current) => ratioOn(next, current));
  };
  // A pick past the room is not taken: an upload ends after the room may have gone.
  const addReference = (image: PickedImage) =>
    setReferences((current) =>
      current.length >= ADVANCED_REFERENCES ||
      current.some((each) => each.assetId === image.assetId)
        ? current
        : [...current, image],
    );
  const total = totalCombinations(picks);
  const request: ImageAdvancedRequest | undefined =
    product === null || total === 0
      ? undefined
      : {
          type: "image.advanced",
          model: model.id,
          aspect_ratio: aspectRatio,
          // Brand logo, typography and colors are Coming soon in the product popover (G8 difference 5).
          product: { product_id: product.productId, image_ids: [product.assetId], colors: false },
          style: style.trim() === "" ? null : style,
          references: advancedReferences(
            picks,
            references.map((each) => each.assetId),
          ),
          brand_logo: false,
          fonts: false,
        };
  return {
    models,
    model,
    chooseModel,
    aspectRatio,
    setAspectRatio,
    product,
    setProduct,
    references,
    setReferences,
    addReference,
    /** A reference removed takes its ticks with it (live's `settings-preview`). */
    removeReference: (assetId: string) => {
      setReferences((current) => current.filter((each) => each.assetId !== assetId));
      setPicks((current) =>
        keepTiles(
          current,
          Object.keys(current).filter((tile) => tile !== assetId),
        ),
      );
    },
    style,
    /** A Style cleared takes the copies ticked for it (G8-advanced-mode difference 3). */
    setStyle: (next: string) => {
      setStyleText(next);
      if (next.trim() === "")
        setPicks((current) =>
          keepTiles(
            current,
            Object.keys(current).filter((tile) => tile !== STYLE_TILE),
          ),
        );
    },
    picks,
    setPicks,
    /** The images the ticks make: live's count. */
    total,
    canSetup: canSetupCopies({
      product: product !== null,
      references: references.length,
      style,
    }),
    /**
     * References arriving must ask first (live's `checkAddReferencesDirectly`): the Style has
     * ticked copies.
     */
    mustAsk: style.trim() !== "" && tileCombinations(picks[STYLE_TILE]) > 0,
    /** "Use Image": the Style and every tick go. */
    clearForImages: () => {
      setStyleText("");
      setPicks({});
    },
    /** What a quote and a start send, once there are ticks and a product. */
    request,
    /** What the simple composer takes back when the switch goes off. */
    carry: (): AdvancedCarry => ({ product, references, modelId: model.id, aspectRatio }),
  };
}

export type Advanced = ReturnType<typeof useAdvanced>;
